import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { Icon } from './icons.js'

/*
 * The classroom's small parts (design/style-c README §2). Six type sizes,
 * one soft-gold primary per screen, lists with 1px dividers instead of boxes,
 * status as a 6px dot plus one word.
 */

type Variant = 'primary' | 'secondary'

export function Button({
  variant = 'primary',
  className = '',
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return <button type={type} className={`cl-btn cl-btn-${variant} ${className}`} {...props} />
}

export function ButtonLink({
  to,
  variant = 'primary',
  className = '',
  children,
}: {
  to: string
  variant?: Variant
  className?: string
  children: ReactNode
}) {
  return (
    <Link to={to} className={`cl-btn cl-btn-${variant} ${className}`}>
      {children}
    </Link>
  )
}

/** A forward text link: „Powtórz ›”. */
export function More({ to, children, quiet = false, className = '' }: { to: string; children: ReactNode; quiet?: boolean; className?: string }) {
  return (
    <Link to={to} className={`${quiet ? 'cl-link-quiet' : 'font-semibold'} whitespace-nowrap ${className}`}>
      {children} ›
    </Link>
  )
}

export function Title({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h1 className="cl-title m-0">{children}</h1>
      {sub && <p className="m-0 text-class-ink-soft">{sub}</p>}
    </section>
  )
}

/** A section heading: 22px on the student side, 18px in Studio. */
export function SectionHead({
  children,
  action,
  count,
  small = false,
}: {
  children: ReactNode
  action?: ReactNode
  count?: number
  small?: boolean
}) {
  return (
    <div className={['flex items-baseline gap-3', small ? 'pb-3' : 'pb-3'].join(' ')}>
      <h2 className={['m-0', small ? 'cl-subheading' : 'cl-heading'].join(' ')}>
        {children}
        {count !== undefined && <span className="ml-2 font-class-text text-class-gloss text-class-ink-faint">{count}</span>}
      </h2>
      {action && <div className="ml-auto text-class-gloss">{action}</div>}
    </div>
  )
}

/** Divider list: every row has a top hairline, the last one a bottom one too. */
export function Rows({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={[
        'flex flex-col [&>*]:border-t [&>*]:border-class-line [&>*:last-child]:border-b',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  )
}

/** Title + one meta line, the way every list row reads. */
export function RowText({ title, meta, className = '' }: { title: ReactNode; meta?: ReactNode; className?: string }) {
  return (
    <span className={`min-w-0 flex-1 leading-snug ${className}`}>
      <span className="block font-semibold">{title}</span>
      {meta && <span className="block text-class-gloss text-class-ink-faint">{meta}</span>}
    </span>
  )
}

/** Thin progress bar, gold fill. `track` is white on the blue hero card. */
export function Bar({ value, track = 'field', className = '' }: { value: number; track?: 'field' | 'white'; className?: string }) {
  const pct = Math.max(0, Math.min(100, value))
  return (
    <span
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={[
        'block h-1.5 overflow-hidden rounded-[3px]',
        track === 'white' ? 'bg-class-white' : 'bg-class-field',
        className,
      ].join(' ')}
    >
      <span className="block h-full bg-class-gold transition-[width]" style={{ width: `${pct}%` }} />
    </span>
  )
}

export type Tone = 'ok' | 'wait' | 'bad' | 'neutral' | 'info'

const toneColor: Record<Tone, string> = {
  ok: 'bg-class-correct',
  wait: 'bg-class-wait',
  bad: 'bg-class-wrong',
  neutral: 'bg-class-ink-faint',
  info: 'bg-class-sky-700',
}

export function Dot({ tone }: { tone: Tone }) {
  return <span aria-hidden className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${toneColor[tone]}`} />
}

/** Studio status: a 6px dot and one word. */
export function Status({ tone, children, className = '' }: { tone: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-class-label font-semibold text-class-ink-soft ${className}`}>
      <Dot tone={tone} />
      {children}
    </span>
  )
}

/** The white number card (Studio and the client card). */
export function Stat({
  label,
  value,
  hint,
  to,
}: {
  label: ReactNode
  value: ReactNode
  hint?: ReactNode
  to?: string
}) {
  const body = (
    <>
      <p className="m-0 text-class-gloss text-class-ink-soft">{label}</p>
      <p className="m-0 mt-1.5 text-class-heading font-bold leading-tight">{value}</p>
      {hint && <p className="m-0 mt-0.5 text-class-label text-class-ink-faint">{hint}</p>}
    </>
  )
  const cls = 'cl-card block p-6 text-class-ink'
  return to ? (
    <Link to={to} className={`${cls} hover:shadow-[inset_0_0_0_1px_var(--color-class-sky-200)]`} style={{ color: 'var(--color-class-ink)' }}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  )
}

export function StatGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-4 desk:grid-cols-3 desk:gap-6">{children}</div>
}

/** Underlined tabs: 14px, 600, the current one ink with a 2px blue line. */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: { value: T; label: ReactNode }[]
  value: T
  onChange: (v: T) => void
  label: string
}) {
  return (
    <div role="tablist" aria-label={label} className="flex flex-wrap items-end gap-x-6 gap-y-1">
      {items.map((it) => {
        const on = it.value === value
        return (
          <button
            key={it.value}
            role="tab"
            type="button"
            aria-selected={on}
            onClick={() => onChange(it.value)}
            className={[
              'cursor-pointer border-0 border-b-2 bg-transparent px-0 pb-1.5 font-class-text text-class-gloss font-semibold',
              on ? 'border-class-sky-700 text-class-ink' : 'border-transparent text-class-ink-faint hover:text-class-ink',
            ].join(' ')}
          >
            {it.label}
          </button>
        )
      })}
    </div>
  )
}

/** Tabs that are links (a filter kept in the URL). */
export function LinkTabs({ items, label }: { items: { to: string; label: ReactNode; end?: boolean }[]; label: string }) {
  return (
    <nav aria-label={label} className="flex flex-wrap items-end gap-x-6 gap-y-1">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          className={({ isActive }) =>
            [
              'border-b-2 pb-1.5 text-class-gloss font-semibold',
              isActive ? 'border-class-sky-700 !text-class-ink' : 'border-transparent !text-class-ink-faint hover:!text-class-ink',
            ].join(' ')
          }
        >
          {it.label}
        </NavLink>
      ))}
    </nav>
  )
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '·'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

export function Avatar({ name, size = 38, className = '' }: { name: string; size?: number; className?: string }) {
  return (
    <span
      aria-hidden
      style={{ width: size, height: size, fontSize: size >= 48 ? 18 : size <= 32 ? 12 : 14 }}
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-class-sky-200 font-bold text-class-sky-700 ${className}`}
    >
      {initials(name)}
    </span>
  )
}

/** The gold "D" mark. */
export function LogoMark({ size = 38 }: { size?: number }) {
  return (
    <span
      aria-hidden
      style={{ width: size, height: size, fontSize: size * 0.47 }}
      className="inline-flex shrink-0 items-center justify-center rounded-class-btn bg-class-gold font-class-display font-semibold text-class-ink"
    >
      D
    </span>
  )
}

/** The rounded search field in a top bar. */
export function SearchBox({
  value,
  onChange,
  onSubmit,
  placeholder,
  label,
  width = 240,
  className = '',
}: {
  value: string
  onChange: (v: string) => void
  onSubmit?: () => void
  placeholder: string
  label: string
  width?: number
  className?: string
}) {
  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit?.()
      }}
      style={{ width }}
      className={`flex h-10 max-w-full items-center gap-2 rounded-full border border-class-field bg-class-white px-3.5 ${className}`}
    >
      <Icon name="search" size={16} className="shrink-0 text-class-ink-faint" />
      <label className="sr-only" htmlFor={`search-${label}`}>
        {label}
      </label>
      <input
        id={`search-${label}`}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full min-w-0 border-0 bg-transparent font-class-text text-class-gloss text-class-ink outline-none placeholder:text-class-ink-faint"
      />
    </form>
  )
}

/** Loading, as the player's states board draws it: soft blue bars. */
export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div aria-busy="true" aria-label="…" className="flex flex-col gap-3">
      <span className="block h-6 w-1/3 animate-pulse rounded-lg bg-class-field" />
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} className="block h-3.5 animate-pulse rounded-md bg-class-field-soft" style={{ width: `${80 - i * 12}%` }} />
      ))}
    </div>
  )
}

/** A failure line: ✗ and words, never colour alone. */
export function ErrorLine({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="m-0 font-semibold text-class-wrong">
      ✗ {children}
    </p>
  )
}

/** A label above a field — 12px, 600. */
export function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-class-label font-semibold text-class-ink-soft">
      {children}
    </label>
  )
}
